import type React from 'react';
import type { FieldValues } from 'react-hook-form';
import Input from 'creative-ui/uikit/Input';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import type { CaptchaResponse, CaptchaWidgetProps } from '../index';

const SimpleCaptchaWidget = <T extends FieldValues = FieldValues>({
  config,
  onCaptchaResponse,
  control,
  setError,
  clearErrors,
  name,
  rules,
  label = '',
  error,
}: CaptchaWidgetProps<T>) => {
  // Sync external Auth0 server error into react-hook-form
  useEffect(() => {
    if (error && setError) {
      setError(name, { type: 'server', message: error });
    }
  }, [error, name, setError]);

  if (config.provider !== 'auth0') {
    return null;
  }

  const imageUrl = config.image;
  const captchaImageAlt = 'CAPTCHA challenge';

  // Handle input change for auth0 v1 (image captcha)
  const handleInputChange = (
    value: string,
    onChange: (value: string) => void,
  ) => {
    // Update react-hook-form state
    onChange(value);

    if (value) {
      clearErrors?.(name);
    }

    const response: CaptchaResponse = {
      provider: 'auth0',
      answer: value,
    };
    onCaptchaResponse(response);
  };

  return (
    <div>
      {imageUrl && <img src={imageUrl} alt={captchaImageAlt} />}

      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field, fieldState }) => (
          <Input
            name={field.name}
            value={field.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleInputChange(e.target.value, field.onChange)
            }
            type="text"
            status={fieldState.error?.message || error ? 'error' : undefined}
            statusMessage={fieldState.error?.message || error}>
            {label}
          </Input>
        )}
      />
    </div>
  );
};

export default SimpleCaptchaWidget;
